import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button, FormControl } from 'components/common';
import { ISecondJobForm } from './types';
import { ApplicationFormPageEnum } from '../types';
import { secondJobQuestions } from './questions';
import { useNavigateBack } from 'hooks';
import { getCurrentRoute } from 'utils/location';
import { useMutation } from '@tanstack/react-query';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { parseApplicationInProgress } from '../helpers';
import { useEffect } from 'react';
import { applicationFormApi } from 'api/applicationForm';

export const SecondJob = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<ISecondJobForm>({
    defaultValues: {
      hasSecondJob: null,
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  const submitFormHandler = async (formData: ISecondJobForm) => {
    const deleteFollowingPages = formattedResponse && formattedResponse.hasSecondJob !== formData.hasSecondJob;
    const nextPage =
      formData.hasSecondJob === 'true' ? ApplicationFormPageEnum.EMPLOYMENT : ApplicationFormPageEnum.OTHER_INCOME;

    const questions = Object.entries(formData).map(([key, answer]) => {
      const isRadioButtonAnswerBoolean = answer === 'false' || 'true';

      return {
        question: intl.formatMessage({ id: secondJobQuestions[key].labelKey }),
        key,
        answer: isRadioButtonAnswerBoolean ? answer === 'true' : answer,
      };
    });

    patchApplicationInProgress({ questions, currentRoute, applicationId, deleteFollowingPages });

    if (formData.hasSecondJob === 'true') searchParams.set('hasSecondJob', `${Boolean(formData.hasSecondJob)}`);
    searchParams.set('section', nextPage);

    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        {Object.entries(secondJobQuestions).map(([key, question]) => (
          <section key={key} className={key}>
            <FormattedMessage id={question.labelKey} tagName={'h4'} />
            {question.explanationKey && <FormattedMessage id={question.explanationKey} tagName={'p'} />}

            <FormControl
              rules={{ validate: (value: any) => (value === '' ? requiredFieldErrorMessage : true) }}
              name={key}
              radioOptions={question.options}
              radioGroupProps={{ className: 'mt-2' }}
              onChangeEffect={(event: React.ChangeEvent<HTMLInputElement>) => {
                form.setValue(key as keyof ISecondJobForm, event.target.value);
              }}
            />
          </section>
        ))}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
